// @flow
/* eslint pinterest/no-data-options-dot-notation:0 */
import * as Cookie from 'app/packages/cookie-utils';
import SeamlessExperiment from 'app/common/lib/SeamlessExperiment';
import storage from 'app/common/lib/Storage';
import urlUtil from 'app/common/lib/urlUtil';
import trackRegisterAction, { flush } from 'app/common/lib/util/trackRegisterAction';

const branchLoader = () => import(/* webpackChunkName: "BranchSDKLibrary" */ 'branch-sdk');

const blacklist = /^\/(join|login)\//;
const deeplinkUrls = {
  branch: {
    interstitialOpenInApp: 'https://bnc.lt/interstitial',
    authUpsellOpenInApp: 'https://bnc.lt/auth-upsell',
    onNavigateOpenInApp: 'https://bnc.lt/unauth-navigate',
    autoRedirect: 'https://bnc.lt/auto-redirect',
    default: 'https://bnc.lt/mweb-default',
  },
};
const branchId = 'key_live_eifqPbvhIRnKEb1T0MSFpipgsCenSDRu';

const BLACKLIST_DEEPLINK_PARAM_KEYS = [
  'access_token',
  'new_password',
  'new_password_confirm',
  'password',
  'next',
];

type DeeplinkUrlPlacement = $Keys<typeof deeplinkUrls.branch>;

// UTILS

/**
 * @return {string} the app we are using as a browser
 */
function currentBrowserApp(userAgent: string) {
  if (userAgent.includes('Twitter for iPhone') || userAgent.includes('TwitterAndroid')) {
    return 'twitter';
  } else if (userAgent.includes('FBAN/FBIOS')) {
    return 'facebook';
  } else if (userAgent.includes('CriOS') || userAgent.includes('Chrome')) {
    return 'chrome';
  }

  return 'unknown';
}

/**
 *
 * @param {string} deepLink deeplink
 * @return {string} the path of a deeplink (pinterest://user/pinterest -> user/pinterest)
 */
function extractDeepLinkPath(deepLink) {
  return deepLink.split('://')[1];
}

/**
 *
 * @param {string} url
 * @param {Object} params
 * @return {string} a url appended with params
 */
function craftUrl(url, params) {
  let src = url + '?';
  if (params) {
    for (const key in params) {
      if (BLACKLIST_DEEPLINK_PARAM_KEYS.includes(key)) {
        trackRegisterAction(`growth_unauth_lib_deeplink_attempt.found_sensitive_key_${key}`);
        flush(false);
        // eslint-disable-next-line no-continue
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(params, key)) {
        src += encodeURIComponent(key) + '=' + encodeURIComponent(params[key]) + '&';
      }
    }
  }
  return src;
}

export function fetchAppInstallData(): Promise<boolean> {
  return new Promise((resolve) => {
    branchLoader().then(({ default: Branch }) => {
      Branch.init(branchId, {}, (err: *, data: *) => {
        const hasApp = !err && data && data.has_app;
        resolve(hasApp);
      });
    });
  });
}

// BRANCH

function branchAddScript(url) {
  url += '&js_embed=true';
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = url;
  document.getElementsByTagName('head')[0].appendChild(script);
}

function branchSendToAppOnLoad(url, params) {
  trackRegisterAction('mweb_launch_deeplink.branchSendToAppOnLoad_called');
  const src = craftUrl(url, params);
  branchAddScript(src);
}

function branchSendToAppOrStoreOnClick(url, params) {
  trackRegisterAction('mweb_launch_deeplink.branchSendToAppOrStoreOnClick_called');
  if (url) {
    const ua = navigator.userAgent;
    // Note: This hack is used because iOS 9 + has different behavior with regards
    // to script injection. Please keep in sync with
    // webapp/app/mobile/lib/deepLink.js in isIOSNineOrGreater
    if (
      (ua.indexOf('iPad') > -1 || ua.indexOf('iPhone') > -1 || ua.indexOf('iPod') > -1) &&
      (ua.indexOf('OS 9') > -1 ||
        ua.indexOf('OS 10') > -1 ||
        ua.indexOf('OS 11') > -1 ||
        ua.indexOf('OS 12') > -1) &&
      ua.indexOf('CriOS') === -1
    ) {
      window.location.assign(craftUrl('https://bnc.lt/a/' + branchId, params));
    } else {
      params.passive_load = false;
      branchSendToAppOnLoad(url, params);
    }
  }
}

function handleBranchDeeplink(
  unauthId: string,
  placement: DeeplinkUrlPlacement,
  customSchemeDeeplinkURL: string,
) {
  trackRegisterAction('mweb_launch_deeplink.branch');
  const deeplinkBaseUrl = deeplinkUrls.branch[placement];
  const referrerUrl = Cookie.get(PCONST.COOKIE_SESSION_REFERRER) || document.referrer;

  const currentPageUrl = urlUtil.removeQueryParams(
    window.location.href,
    BLACKLIST_DEEPLINK_PARAM_KEYS,
  );

  let branchParams = {
    $deeplink_path: extractDeepLinkPath(customSchemeDeeplinkURL),
    current_page_url: currentPageUrl,
    referrer_url: referrerUrl,
    install_id: unauthId,
    utm_source: PCONST.thrift.ViewType.SERVICE_ENTRY_WEB,
    utm_medium: PCONST.thrift.ViewParameterType.SERVICE_ENTRY_WEB_BRANCH,
  };

  trackRegisterAction(
    `mweb_launch_deeplink.branch.${unauthId ? 'with_unauthId' : 'without_unauthId'}`,
  );

  branchParams = Object.assign({}, branchParams, urlUtil.getQueryStringParams());
  if (placement === 'autoRedirect') {
    branchSendToAppOnLoad(deeplinkBaseUrl, branchParams);
    return;
  }

  branchSendToAppOrStoreOnClick(deeplinkBaseUrl, branchParams);
}

export const deeplinkForPlatform = (
  platform: string,
  androidDeeplink: ?string,
  iOSDeeplink: ?string,
): ?string => {
  if (platform === 'ios' || platform === 'ipad') {
    return iOSDeeplink;
  } else if (platform === 'android') {
    return androidDeeplink;
  }

  return null;
};

export default class DeepLink {
  currentBrowserApp: string;
  deeplinkService = 'branch';
  platform: string;

  constructor(rawUserAgent: string, platform: string) {
    this.platform = platform;
    this.currentBrowserApp = currentBrowserApp(rawUserAgent);
  }

  shouldDeepLink(
    referrer: string,
    userAgent: { canUseNativeApp: boolean, isTablet: boolean },
    isAuthenticated: boolean,
  ) {
    if (SeamlessExperiment.shouldDisable(referrer, true)) {
      return false;
    }

    if (!userAgent.canUseNativeApp || userAgent.isTablet) {
      return false;
    }

    if (isAuthenticated) {
      // Adding this flag in local storage as we do not need it to be sent on every request
      if (
        !storage.localStorage.getItem(
          PCONST.LOCALSTORAGE_HAS_BEEN_AUTH_REDIRECTED_TO_APP_ON_NAVIGATE,
        )
      ) {
        storage.localStorage.setItem(
          PCONST.LOCALSTORAGE_HAS_BEEN_AUTH_REDIRECTED_TO_APP_ON_NAVIGATE,
          true,
        );
        return true;
      }
      return false;
    }

    return true;
  }

  attemptAutoRedirectToApp(
    unauthId: string,
    referrer: string,
    isAuthenticated: boolean,
    userAgent: { canUseNativeApp: boolean, isTablet: boolean },
    customSchemeDeeplink: string,
  ) {
    if (!this.shouldDeepLink(referrer, userAgent, isAuthenticated)) {
      return;
    }

    this.redirectToApp(unauthId, 'autoRedirect', customSchemeDeeplink);
  }

  redirectToApp(
    unauthId: string,
    placement: DeeplinkUrlPlacement,
    customSchemeDeeplinkURL: string = '',
  ) {
    if (
      placement !== 'interstitialOpenInApp' &&
      window.location &&
      blacklist.test(window.location.pathname) &&
      customSchemeDeeplinkURL
    ) {
      return;
    }

    // get the base url depending on the placement
    placement = placement || 'default';

    handleBranchDeeplink(unauthId, placement, customSchemeDeeplinkURL);
  }

  navigateToDeepLink(
    unauthId: string,
    deeplinkUrls: { androidDeeplink: string, iOSDeeplink: string },
  ) {
    trackRegisterAction('seamless.' + this.platform);
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent('seamlessClick', true, true, {});
    window.dispatchEvent(evt);
    const customSchemeDeeplinkUrl = // Default to the iOS deeplink if the platform is not android
      this.platform === 'android' ? deeplinkUrls.androidDeeplink : deeplinkUrls.iOSDeeplink;
    this.redirectToApp(unauthId, 'onNavigateOpenInApp', customSchemeDeeplinkUrl);
  }
}
